import React, { useState, useEffect } from 'react';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Bot, Save, RotateCcw, GripVertical, Edit, Cloud, Laptop, Sparkles } from 'lucide-react';
import { AIModel } from '@/types';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ProviderLogoUploadDialog from './ProviderLogoUploadDialog';
import { supabase } from '@/integrations/supabase/client';

interface ModelSettingsData {
  providerOrder: string[];
}

interface SortableProviderItemProps {
  id: string;
  displayName: string;
  logo: React.ReactNode;
  onEdit: () => void;
  isEditable: boolean;
}

const SortableProviderItem: React.FC<SortableProviderItemProps> = ({ id, displayName, logo, onEdit, isEditable }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-3 border rounded-md bg-background"
    >
      <div className="flex items-center gap-3">
        <div {...attributes} {...listeners} className="cursor-grab">
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="w-6 h-6 flex items-center justify-center">{logo}</div>
        <span className="font-medium">{displayName}</span>
      </div>
      {isEditable && (
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Logo
        </Button>
      )}
    </div>
  );
};

const ModelSettings = () => {
  const { models, updateModel, updateProviderModels, fetchModels } = useSupabaseStore();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [providerOrder, setProviderOrder] = useState<string[]>(['cohere', 'openai', 'gemini', 'deepseek', 'maplept', 'ollama_remote', 'ollama_local', 'custom']);
  const [selectedProvider, setSelectedProvider] = useState<{ id: string; name: string } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const saved = localStorage.getItem('modelSettings');
    if (saved) {
      const settings: ModelSettingsData = JSON.parse(saved);
      const defaultOrder = ['cohere', 'openai', 'gemini', 'deepseek', 'maplept', 'ollama_remote', 'ollama_local', 'custom'];
      const currentProviders = new Set(settings.providerOrder);
      const newProviders = defaultOrder.filter(p => !currentProviders.has(p));
      setProviderOrder([...settings.providerOrder, ...newProviders]);
    }
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setProviderOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleReset = () => {
    const defaultOrder = ['cohere', 'openai', 'gemini', 'deepseek', 'maplept', 'ollama_remote', 'ollama_local', 'custom'];
    setProviderOrder(defaultOrder);
    localStorage.removeItem('modelSettings');
    toast({
      title: "Settings Reset",
      description: "Model provider preferences have been reset to default.",
    });
  };

  const handleSave = () => {
    const settings: ModelSettingsData = { providerOrder };
    localStorage.setItem('modelSettings', JSON.stringify(settings));
    toast({
      title: "Settings Saved",
      description: "Model provider preferences have been saved.",
    });
  };

  const handleEditLogo = (providerId: string, providerName: string) => {
    setSelectedProvider({ id: providerId, name: providerName });
    setIsDialogOpen(true);
  };

  const handleSaveLogo = async (newUrl: string) => {
    if (selectedProvider) {
      try {
        // Ensure MaplePT exists in DB before updating logo, admin-only
        if (selectedProvider.id === 'maplept') {
          await supabase.functions.invoke('ensure-maplept-model', {
            body: {
              name: 'MaplePT-Mini',
              apiName: '/app/canxpai-maplept-mini-v1',
              tier: 'basic',
              is_active: true,
              logo_url: newUrl,
            },
          });
        }

        await updateProviderModels(selectedProvider.id, { logo_url: newUrl });
        await fetchModels(); // Refresh to show the updated logo immediately
        toast({ title: "Success", description: `${selectedProvider.name} logo updated.` });
      } catch (error) {
        // Error is handled in the store or by the function
      }
    }
    setIsDialogOpen(false);
  };

  const handleToggleActive = async (model: AIModel, isActive: boolean) => {
    try {
      await updateModel(model.id, { is_active: isActive });
      toast({
        title: "Model Updated",
        description: `${model.name} has been ${isActive ? 'enabled' : 'disabled'}.`,
      });
    } catch (error) {
      // Error toast is handled in the store
    }
  };

  const providerDisplayMap: Record<string, { name: string; defaultLogo: React.ReactNode; isEditable: boolean }> = {
    'cohere': { name: 'Cohere', defaultLogo: <AvatarImage src="/images/models/cohere.svg" />, isEditable: true },
    'openai': { name: 'OpenAI', defaultLogo: <AvatarImage src="/images/models/openai.svg" />, isEditable: true },
    'gemini': { name: 'Gemini', defaultLogo: <AvatarImage src="/images/models/gemini.png" />, isEditable: true },
    'deepseek': { name: 'DeepSeek', defaultLogo: <AvatarImage src="/images/models/deepseek.png" />, isEditable: true },
    'maplept': { name: 'MaplePT', defaultLogo: <AvatarImage src="/images/models/maplept.svg" />, isEditable: true },
    'ollama_remote': { name: 'Ollama', defaultLogo: <Cloud className="h-full w-full text-blue-600" />, isEditable: true },
    'ollama_local': { name: 'Local Ollama', defaultLogo: <Laptop className="h-full w-full text-green-600" />, isEditable: false },
    'custom': { name: 'Custom', defaultLogo: <Sparkles className="h-full w-full text-muted-foreground" />, isEditable: false },
  };

  const getProviderLogo = (provider: string) => {
    const modelForProvider = models.find(m => m.provider === provider);
    const logoUrl = modelForProvider?.logo_url;
    const displayInfo = providerDisplayMap[provider];
    if (logoUrl) {
      return <AvatarImage src={logoUrl} alt={displayInfo.name} />;
    }
    return displayInfo.defaultLogo;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Model Provider Preferences</CardTitle>
          <CardDescription>
            Drag to reorder providers and click edit to upload their logos. Your preferences are saved locally.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 mb-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={providerOrder}
                strategy={verticalListSortingStrategy}
              >
                {providerOrder.map((provider) => {
                  const displayInfo = providerDisplayMap[provider];
                  if (!displayInfo) return null;
                  return (
                    <SortableProviderItem
                      key={provider}
                      id={provider}
                      displayName={displayInfo.name}
                      logo={
                        <Avatar className="w-6 h-6">
                          {getProviderLogo(provider)}
                          <AvatarFallback>{displayInfo.name.substring(0, 2)}</AvatarFallback>
                        </Avatar>
                      }
                      isEditable={displayInfo.isEditable}
                      onEdit={() => handleEditLogo(provider, displayInfo.name)}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Preferences
            </Button>
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Default
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>AI Model Management</CardTitle>
          <CardDescription>
            Enable or disable specific AI models available in the application.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {models.filter(m => m.provider !== 'ollama').map(model => (
              <div key={model.id} className="flex items-center justify-between p-3 border rounded-md">
                <div className="flex items-center gap-4">
                  <Avatar>
                    <AvatarImage src={model.logo_url || undefined} />
                    <AvatarFallback>
                      <Bot size={20} />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">{model.name}</p>
                    <p className="text-sm text-muted-foreground">{model.provider}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id={`active-switch-${model.id}`}
                      checked={!!model.is_active}
                      onCheckedChange={(checked) => handleToggleActive(model, checked)}
                    />
                    <Label htmlFor={`active-switch-${model.id}`} className="text-sm">
                      {model.is_active ? 'Enabled' : 'Disabled'}
                    </Label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {selectedProvider && (
        <ProviderLogoUploadDialog
          isOpen={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          providerName={selectedProvider.name}
          currentLogoUrl={models.find(m => m.provider === selectedProvider.id)?.logo_url || ''}
          onSave={handleSaveLogo}
        />
      )}
    </div>
  );
};

export default ModelSettings;